import { useEffect, useRef, useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { useRenameConversation } from "@/hooks/queries";

export function ConversationTitle({ conversation }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);
  const rename = useRenameConversation();

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  if (!conversation) return <div className="h-4 w-40 animate-pulse rounded bg-white/[0.06]" />;

  const save = async () => {
    const title = draft.trim();
    setEditing(false);
    if (!title || title === conversation.title) return;
    try {
      await rename.mutateAsync({ id: conversation.id, title });
      toast.success("Conversation renamed");
    } catch (e) {
      toast.error(e.message || "Couldn't rename the conversation");
    }
  };

  if (editing) {
    return (
      <div className="flex min-w-0 max-w-md flex-1 items-center gap-1">
        <input
          ref={inputRef}
          value={draft}
          maxLength={200}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") setEditing(false);
          }}
          data-testid="conversation-title-input"
          className="h-8 min-w-0 flex-1 rounded-md border border-white/15 bg-ink-850 px-2 text-sm text-white focus:border-white/30 focus:outline-none"
        />
        <button type="button" onClick={save} aria-label="Save title" data-testid="conversation-title-save" className="rounded-md p-1.5 text-zinc-400 hover:bg-white/[0.06] hover:text-white">
          <Check className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => setEditing(false)} aria-label="Cancel rename" data-testid="conversation-title-cancel" className="rounded-md p-1.5 text-zinc-400 hover:bg-white/[0.06] hover:text-white">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(conversation.title);
        setEditing(true);
      }}
      data-testid="conversation-title"
      className="group flex min-w-0 items-center gap-2 rounded-md px-2 py-1 text-left transition-colors hover:bg-white/[0.05]"
      title="Rename conversation"
    >
      <span className="truncate text-[14px] font-medium text-zinc-100">{conversation.title}</span>
      <Pencil className="h-3.5 w-3.5 shrink-0 text-zinc-600 opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
}
